/* SpawnAnything — thrown explosives (simulation side): the god tools "Granate" and "Sprengfass" fly in as real objects.

   The sim owns the flight. A throw command carries the start point, the target and the flight time in ticks (all
   integers: positions in 1/16 m, like every other lockstep position). It is applied at tick k like any other command;
   the object then counts its age once per tick and lands in tick k + d, where the ordinary tool explosion (bomb() in
   sim.worker.js) happens at the target. The trajectory itself is pure presentation: the renderer (src/fx/thrown.js)
   draws the arc from the same start / target / age, so the sim only has to agree on *when* and *where* it lands.

   Commands (see applySp / applyCmd in sim.worker.js):
     single player / replay: { cmd: 'throw', k, sx, sy, sz, x, z, d, dmg, aoe, knock, kind }  (tool values from main.js)
     lockstep:               { t: 'throw', k, sx, sy, sz, x, z, d }  (tool values from the setup's tool table)
   k = 'granate' | 'fass', (sx, sy, sz) = start, (x, z) = target (all /16 m), d = flight ticks (THROW_MIN..THROW_MAX).

   The state is a plain list (no randomness, no R() calls) and goes into saveState/loadState (featSave/featLoad) and,
   while anything is in flight, into rpHash/stateHash (featHash) — battles without throws keep their old hashes. */

export const THROW_KINDS = { granate: 0, fass: 1 }; // tool key -> visual kind (0 grenade, 1 barrel)
export const THROW_CAP = 64, THROW_MIN = 8, THROW_MAX = 45; // objects in flight; flight ticks (0.27 .. 1.5 s at 30 Hz)
const QPOS = 1 << 20, REACH = 400 * 16; // positions as in lockstep; start at most 400 m (horizontally) from the target
const qi = (v, lo, hi) => Number.isInteger(v) && v >= lo && v <= hi;

export function createThrown() { return { list: [], seq: 0 }; }
export function thrownReset(S) { S.list.length = 0; S.seq = 0; }

/** geometry / kind check shared by both command paths (no side effects) */
export function thrownValid(S, c) {
  if (!c || typeof c.k !== 'string' || THROW_KINDS[c.k] === undefined || S.list.length >= THROW_CAP) return false;
  if (!qi(c.sx, -QPOS, QPOS) || !qi(c.sy, -QPOS, QPOS) || !qi(c.sz, -QPOS, QPOS) || !qi(c.x, -QPOS, QPOS) || !qi(c.z, -QPOS, QPOS)) return false;
  if (!qi(c.d, THROW_MIN, THROW_MAX) || Math.abs(c.sx - c.x) > REACH || Math.abs(c.sz - c.z) > REACH) return false;
  return true;
}

/** start a flight (call thrownValid first). T = { dmg, aoe, knock, kind } of the tool, slot = thrower's player slot */
export function thrownAdd(S, c, T, slot) {
  S.list.push({ id: ++S.seq, vk: THROW_KINDS[c.k], sx: c.sx, sy: c.sy, sz: c.sz, x: c.x, z: c.z, d: c.d,
    age: -1, // the tick that applies the command makes it 0 (also when it was applied while paused: same tick in a replay)
    slot, dmg: +T.dmg, aoe: +T.aoe, knock: +T.knock, kind: +T.kind });
  return true;
}

/** once per tick: age every object; land(o) for those whose flight is over (in throw order) */
export function thrownStep(S, land) {
  if (!S.list.length) return;
  for (let k = 0; k < S.list.length;) {
    const o = S.list[k];
    if (++o.age >= o.d) { S.list.splice(k, 1); land(o); } else k++;
  }
}

export const thrownSave = S => ({ list: S.list.map(o => ({ ...o })), seq: S.seq });
export function thrownLoad(S, f) { S.list = f ? f.list.map(o => ({ ...o })) : []; S.seq = f ? f.seq : 0; } // older snapshots: nothing in flight

/** hash input while something flies (null otherwise, so old replays keep their checkpoints) */
export function thrownHash(S) {
  if (!S.list.length) return null;
  const a = new Float64Array(S.list.length * 14); let k = 0;
  for (const o of S.list) { a[k++] = o.id; a[k++] = o.vk; a[k++] = o.sx; a[k++] = o.sy; a[k++] = o.sz; a[k++] = o.x; a[k++] = o.z; a[k++] = o.d; a[k++] = o.age; a[k++] = o.slot; a[k++] = o.dmg; a[k++] = o.aoe; a[k++] = o.knock; a[k++] = o.kind; }
  return a;
}

export const THROW_STRIDE = 11;
/** renderer data per object: start xyz, target x, ground y, z (m), age, d, visual kind, id, own (thrown by slot me) */
export function thrownFrame(S, gAt, me) {
  const n = S.list.length; if (!n) return null;
  const f = new Float32Array(n * THROW_STRIDE);
  for (let k = 0; k < n; k++) {
    const o = S.list[k], j = k * THROW_STRIDE, x = o.x / 16, z = o.z / 16;
    f[j] = o.sx / 16; f[j + 1] = o.sy / 16; f[j + 2] = o.sz / 16; f[j + 3] = x; f[j + 4] = gAt(x, z); f[j + 5] = z;
    f[j + 6] = o.age; f[j + 7] = o.d; f[j + 8] = o.vk; f[j + 9] = o.id; f[j + 10] = o.slot === me ? 1 : 0;
  }
  return f;
}
